<script setup lang="ts">
import { computed, type Component } from 'vue'
import { PhoneCall } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

interface Props {
  loading?: boolean
  overlay?: boolean
  /** Position/size classes when `overlay` is true (default: full container). */
  overlayClass?: string
  text?: string
  size?: 'sm' | 'md' | 'lg'
  icon?: Component
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  overlay: false,
  size: 'md'
})

const effectiveIcon = computed(() => props.icon || PhoneCall)
</script>

<template>
  <transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="loading"
      :class="
        cn(
          'flex flex-col items-center justify-center gap-4',
          overlay &&
            cn('absolute z-50 bg-background/75 backdrop-blur-sm', props.overlayClass ?? 'inset-0')
        )
      "
    >
      <div
        :class="
          cn(
            'flex flex-col items-center gap-3 rounded-full bg-background/90 shadow-xl border border-border/60 transition-all',
            size === 'sm' && 'p-2',
            size === 'md' && 'p-3',
            size === 'lg' && 'p-6'
          )
        "
      >
        <div class="relative flex items-center justify-center">
          <div
            :class="
              cn(
                'absolute rounded-full bg-primary/20 blur-sm animate-pulse',
                size === 'sm' && 'size-6',
                size === 'md' && 'size-8',
                size === 'lg' && 'size-12'
              )
            "
          ></div>
          <component
            :is="effectiveIcon"
            :class="
              cn(
                'relative text-primary animate-pulse-subtle',
                size === 'sm' && 'size-3',
                size === 'md' && 'size-4',
                size === 'lg' && 'size-8'
              )
            "
          />
        </div>
      </div>
      <span
        v-if="text"
        class="text-[10px] font-medium uppercase tracking-widest text-foreground/90 animate-pulse"
      >
        {{ text }}
      </span>
    </div>
  </transition>
</template>

<style scoped>
@keyframes pulse-subtle {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.92);
    opacity: 0.85;
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
